import React from 'react';
import { SearchDropdown } from '../../../../../components';
import { Msp } from '../../../../../types';
import { NewExistingSegment, NewExistingValue } from '../../../../../components/shared';

interface MSPSelectorProps {
  onMspBussSelect: (value: number) => void;
  onMspBreakerSelect: (value: number) => void;
  onMspManufacturerSelect: (value: string) => void;
  onMspLocationSelect: (value: string) => void;
  onMspCadLocationSelect: (value: string) => void;
  onMspStyleSelect: (value: string) => void;
  onMspTypeSelect: (value: string) => void;
  onMspNewExistingChange: (value: NewExistingValue) => void;
  onMainBreakerNewExistingChange: (value: NewExistingValue) => void;
  selectedMspBuss?: number;
  selectedMspBreaker?: number;
  selectedMspManufacturer?: string;
  selectedMspLocation?: string;
  selectedMspCadLocation?: string;
  selectedMspStyle?: string;
  selectedMspType?: string;
  mspNewExisting?: NewExistingValue;
  mainBreakerNewExisting?: NewExistingValue;
  mspBussOptions: Msp[];
  mspBreakerOptions: Msp[];
  mspManufacturerOptions: Msp[];
  mspLocationOptions: Msp[];
  mspCadLocationOptions: Msp[];
  mspStyleOptions: Msp[];
  mspTypeOptions: Msp[];
  isLoading: boolean;
  error?: string;
  isEnabled: boolean;
}

export const MSPSelector: React.FC<MSPSelectorProps> = ({
  onMspBussSelect,
  onMspBreakerSelect,
  onMspManufacturerSelect,
  onMspLocationSelect,
  onMspCadLocationSelect,
  onMspStyleSelect,
  onMspTypeSelect,
  onMspNewExistingChange,
  onMainBreakerNewExistingChange,
  selectedMspBuss,
  selectedMspBreaker,
  selectedMspManufacturer,
  selectedMspLocation,
  selectedMspCadLocation,
  selectedMspStyle,
  selectedMspType,
  mspNewExisting,
  mainBreakerNewExisting,
  mspBussOptions,
  mspBreakerOptions,
  mspManufacturerOptions,
  mspLocationOptions,
  mspCadLocationOptions,
  mspStyleOptions,
  mspTypeOptions,
  isLoading,
  error,
  isEnabled
}) => {

  const handleMspBussSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMspBussSelect(0);
      return;
    }
    const mspBuss = mspBussOptions?.find(m => m.id === option.id);
    if (mspBuss) {
      onMspBussSelect(parseInt(mspBuss.value, 10));
    }
  };

  const handleMspBreakerSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMspBreakerSelect(0);
      return;
    }
    const mspBreaker = mspBreakerOptions?.find(m => m.id === option.id);
    if (mspBreaker) {
      onMspBreakerSelect(parseInt(mspBreaker.value, 10));
    }
  };

  const handleMspManufacturerSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMspManufacturerSelect('');
      return;
    }
    const mspManufacturer = mspManufacturerOptions?.find(m => m.id === option.id);
    if (mspManufacturer) {
      onMspManufacturerSelect(mspManufacturer.value);
    }
  };

  const handleMspLocationSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMspLocationSelect('');
      return;
    }
    const mspLocation = mspLocationOptions?.find(m => m.id === option.id);
    if (mspLocation) {
      onMspLocationSelect(mspLocation.value);
    }
  };

  const handleMspCadLocationSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMspCadLocationSelect('');
      return;
    }
    const mspCadLocation = mspCadLocationOptions?.find(m => m.id === option.id);
    if (mspCadLocation) {
      onMspCadLocationSelect(mspCadLocation.value);
    }
  };

  const handleMspStyleSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMspStyleSelect('');
      return;
    }
    const mspStyle = mspStyleOptions?.find(m => m.id === option.id);
    if (mspStyle) {
      onMspStyleSelect(mspStyle.value);
    }
  };

  const handleMspTypeSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMspTypeSelect('');
      return;
    }
    const mspType = mspTypeOptions?.find(m => m.id === option.id);
    if (mspType) {
      onMspTypeSelect(mspType.value);
    }
  };

  const mspBussDropdownOptions = mspBussOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const mspBreakerDropdownOptions = mspBreakerOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const mspManufacturerDropdownOptions = mspManufacturerOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const mspLocationDropdownOptions = mspLocationOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const mspCadLocationDropdownOptions = mspCadLocationOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const mspStyleDropdownOptions = mspStyleOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const mspTypeDropdownOptions = mspTypeOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs">Select a task to configure MSP details</p>
      </div>
    );
  }

  return (
    <div className="bg-transparent p-0">
      <div className="space-y-4">
      {/* Row 1: MSP Buss Rating */}
      <div className="grid grid-cols-1 gap-3 items-start">
        <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                MSP Buss Rating
              </label>
            {isEnabled && (
              <NewExistingSegment
                value={mspNewExisting || null}
                onChange={onMspNewExistingChange}
                disabled={!isEnabled}
              />
            )}
          </div>
          <SearchDropdown
            options={mspBussDropdownOptions}
            onSelect={handleMspBussSelect}
            placeholder="Choose MSP buss rating..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedMspBuss ? {
              id: mspBussOptions.find(opt => parseInt(opt.value, 10) === selectedMspBuss)?.id || '',
              label: selectedMspBuss.toString()
            } : null}
            disabled={!isEnabled}
          />
        </div>

        <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                MSP Breaker Rating
              </label>
            {isEnabled && (
              <NewExistingSegment
                value={mainBreakerNewExisting || null}
                onChange={onMainBreakerNewExistingChange}
                disabled={!isEnabled}
              />
            )}
          </div>
          <SearchDropdown
            options={mspBreakerDropdownOptions}
            onSelect={handleMspBreakerSelect}
            placeholder="Choose MSP breaker rating..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedMspBreaker ? {
              id: mspBreakerOptions.find(opt => parseInt(opt.value, 10) === selectedMspBreaker)?.id || '',
              label: selectedMspBreaker.toString()
            } : null}
            disabled={!isEnabled}
          />
        </div>
      </div>

      {/* Row 2: MSP Location */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            MSP Location
          </label>
          <SearchDropdown
            options={mspCadLocationDropdownOptions}
            onSelect={handleMspCadLocationSelect}
            placeholder="Choose MSP location..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedMspCadLocation ? { 
              id: mspCadLocationOptions.find(opt => opt.value === selectedMspCadLocation)?.id || '', 
              label: selectedMspCadLocation 
            } : null}
            disabled={!isEnabled}
          />
        </div>
        
      </div>

      {/* Row 3: MSP Location Note */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            MSP Location Note
          </label>
          <SearchDropdown
            options={mspLocationDropdownOptions}
            onSelect={handleMspLocationSelect}
            placeholder="Choose MSP location note..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedMspLocation ? { 
              id: mspLocationOptions.find(opt => opt.value === selectedMspLocation)?.id || '', 
              label: selectedMspLocation 
            } : null}
            disabled={!isEnabled}
          />
        </div>
      </div>

      {/* Row 4: MSP Manufacturer */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            MSP Manufacturer
          </label>
          <SearchDropdown
            options={mspManufacturerDropdownOptions}
            onSelect={handleMspManufacturerSelect}
            placeholder="Choose MSP manufacturer..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedMspManufacturer ? { 
              id: mspManufacturerOptions.find(opt => opt.value === selectedMspManufacturer)?.id || '', 
              label: selectedMspManufacturer 
            } : null}
            disabled={!isEnabled}
          />
        </div>
      </div>

      {/* Row 5: MSP Style & MSP Type */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-6">
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            MSP Style
          </label>
          <SearchDropdown
            options={mspStyleDropdownOptions}
            onSelect={handleMspStyleSelect}
            placeholder="Choose MSP style..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedMspStyle ? { 
              id: mspStyleOptions.find(opt => opt.value === selectedMspStyle)?.id || '', 
              label: selectedMspStyle 
            } : null}
            disabled={!isEnabled}
          />
        </div>
        
        <div className="col-span-6">
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            MSP Type
          </label>
          <SearchDropdown
            options={mspTypeDropdownOptions}
            onSelect={handleMspTypeSelect}
            placeholder="Choose MSP type..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedMspType ? { 
              id: mspTypeOptions.find(opt => opt.value === selectedMspType)?.id || '', 
              label: selectedMspType 
            } : null}
            disabled={!isEnabled}
          />
        </div>
      </div>

    </div>
    </div>
  );
};
